import { DcBaseComponent } from '@deleteagency/dc';
import CustomAcitivityHelper from 'components/custom-activity-logger/js/custom-activity-helper';
import GtmHelper from 'components/gtm-trigger/js/gtm-helper';
import { formatNumber, formatDecimal } from 'general/js/utils';


const REGEXP_NOT_DIGIT = /[^0-9\.]+/g;

export default class MortgageCalculatorComponent extends DcBaseComponent {

    constructor(...args) {
        super(...args);

        this.gtm = this.options.gtm || {};
    }

    static getNamespace() {
        return 'mortgage-calculator';
    }

    static getRequiredRefs() {
        return [
            'form',
            'price',
            'deposit',
            'interest',
            'duration',
            'monthly',
            'interestOnly',
            'back'
        ];
    }

    get price() {
        return this.refs.price.value;
    }

    get deposit() {
        return this.refs.deposit.value;
    }

    get interest() {
        return this.refs.interest.value;
    }

    get duration() {
        return this.refs.duration.value;
    }

    get loanAmount() {
        return Number(this.price.replace(REGEXP_NOT_DIGIT, '')) - Number(this.deposit.replace(REGEXP_NOT_DIGIT, ''));
    }

    set monthly(val) {
        this.refs.monthly.value = val;
    }

    set interestOnly(val) {
        this.refs.interestOnly.value = val;
    }

    onInit() {
        this.addListener(this.refs.form, 'submit', this._onSubmit);
        this.addListener(this.refs.back, 'click', this._onBack);
        this._initInputs();
    }

    _initInputs() {
        this._formatInput(this.refs.price);
        this._formatInput(this.refs.deposit);
        this._formatDecimalInput(this.refs.interest);
    }

    _formatInput = (element) => {
        const getValue = element.value;
        element.value = formatNumber(getValue);
        element.addEventListener('input', this._handleInputChange);
    }

    _formatDecimalInput = (element) => {
        const getValue = element.value;
        element.value = parseFloat(getValue).toFixed(2);
        element.addEventListener('input', this._handleInterestChange);
    }

    _handleInputChange = (event) => {
        const { value } = event.target;        
        if (value.startsWith('0')) {
            event.target.value = '';
            return;
        }
        const numericValue = value.replace(/[^0-9]/g, '');
        if (numericValue.startsWith('-')) {
            event.target.value = '';
            return;
        }
        event.target.value = formatNumber(numericValue.substring(0, 10));
    };

    _handleInterestChange = (event) => {
        let { value } = event.target;
        value = value.replace(/[^0-9\.]/g, '');

        if (value > 99.99) {
            event.target.value = '99.99';
            return;
        }

        if (value.includes('.')) {
            const [integer, decimal] = value.split('.');
            if (decimal.length > 2) {
                event.target.value = `${integer}.${decimal.substring(0, 2)}`;
                return;
            }
        }

        event.target.value = value;
    };

    _onBack = () => {
        this._showForm();
    };

    _onSubmit = (e) => {
        e.preventDefault();
        this._showResultsIfValid();
    };

    _showResultsIfValid() {
        const interest = this.interest;
        const length = this.duration;
        const amount = this.loanAmount;
        if (amount < 0) {
            this._showError('Your deposit can\'t be more than the property price.');
            return;
        }

        const inputErrors = this._getInputErrors(interest, amount);
        if (inputErrors.length > 0) {
            this._showErrors(inputErrors);
            return;
        }

        const monthlyPayment = this._getMonthlyPayment(interest, length, amount);
        if (Number.isNaN(monthlyPayment)) {
            this._showError('Please enter numbers only');
            return;
        }

        this.monthly = formatDecimal(monthlyPayment);
        this.interestOnly = formatDecimal(this._getInterest(interest, amount));
        this._showResults();
    }

    _showResults() {
        this.element.classList.add('is-results-visible');
        GtmHelper.send(null, { action: 'Mortgage calculator', label: 'Mortgage calculator' });
        this._handleCustomActivityTracking();
    }

    _showForm() {
        this.element.classList.remove('is-results-visible');
    }

    _getInputErrors(interest, amount) {
        const errors = [];
        if (interest < 0) {
            errors.push('The interest rate needs to be positive.');
        } else if (interest === '' || interest === 0) {
            errors.push('The interest rate needs to be filled in.');
        }

        if (amount < 0) {
            errors.push('The loan amount needs to be positive.');
        } else if (amount === '' || amount === 0) {
            errors.push('The loan amount needs to be filled in.');
        }

        return errors;
    }

    _getMonthlyPayment(interest, length, amount) {
        const formulaInterest = interest / (12.0 * 100.0);
        const months = length * 12;
        const monthlyPayment = formulaInterest * amount / (1 - Math.pow((1 + formulaInterest), -1 * months));
        return monthlyPayment.toFixed(2);
    }

    _getInterest(interest, amount) {
        return (interest * amount / (12.0 * 100.0)).toFixed(2);
    }

    _showErrors(errors) {
        this._showError(errors.join(', '));
    }

    _showError(error) {
        alert(error);
    }

    _handleCustomActivityTracking() {
        const pageActivityLogger = document.querySelector('[data-dc-custom-activity-logger][data-page-level]');

        if (pageActivityLogger) {
            const { detailId, itemId, valueString } = pageActivityLogger.dataset;

            const items = {
                PropertyPrice: this.price,
                Deposit: this.deposit,
                InterestRate: this.interest,
                PaidBack: this.duration
            }

            const newValueString = CustomAcitivityHelper.addItemsToValueString(valueString, items);

            const params = {
                activityTitle: 'Calculated Mortgage',
                activityType: 'CalculatedMortgage',
                detailId,
                itemId,
                valueString: newValueString
            }

            CustomAcitivityHelper.send(params);
        }
    }
}
